type RectWithDimensions = {
  height: number;
  width: number;
  left?: number;
  top?: number;
};

type RectWithCoordinates = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type Rect = RectWithDimensions | RectWithCoordinates;

/** Last-resort single-line height, in CSS pixels, used only when the
 *  highlight holds no text line to measure against. Above it a rect spans
 *  more than one line at 100% zoom, so the orphan rules below don't apply. */
const SINGLE_LINE_MAX_HEIGHT = 20;

/** A single-line rect narrower than this share of its own height holds less
 *  than one character at that font size. */
const ORPHAN_WIDTH_RATIO = 0.75;

/** Up to this share of its own height, a rect holds about one glyph plus the
 *  spaces around it — a checkbox (☐), a bullet, a lone digit. */
const GLYPH_WIDTH_RATIO = 1.6;

/** A neighbour this much wider than the speck is real text, not a fragment. */
const NEIGHBOUR_WIDTH_RATIO = 3;

/** A neighbour further than this share of a line-height away belongs to
 *  another column or cell, not to the run of text the glyph was split out of
 *  — a font change mid-sentence leaves essentially no gap. */
const NEIGHBOUR_GAP_RATIO = 0.6;

/** Text selection emits one rect per line, so a rect this many line-heights
 *  tall is not a line of text. */
const MULTI_LINE_HEIGHT_RATIO = 2;

/** …and one that narrow, next to text, is a column rule. */
const VERTICAL_RULE_RATIO = 0.5;

type Metrics = {
  width: number;
  height: number;
  top?: number;
  left?: number;
};

const measure = (rect: Rect): Metrics =>
  'x1' in rect && 'y1' in rect
    ? {
        width: rect.x2 - rect.x1,
        height: rect.y2 - rect.y1,
        top: rect.y1,
        left: rect.x1,
      }
    : {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
      };

/** Rects wider than they are tall are text lines; the shortest of them gives
 *  the line height to measure the rest of the highlight against. */
const textLineHeight = (metrics: Metrics[]): number | undefined => {
  const heights = metrics
    .filter((m) => m.width > m.height && m.height > 0)
    .map((m) => m.height);
  return heights.length > 0 ? Math.min(...heights) : undefined;
};

/** Two rects sit on the same line when their vertical centres are closer
 *  together than half the shorter one's height. */
const sharesLine = (a: Metrics, b: Metrics): boolean => {
  if (a.top === undefined || b.top === undefined) return false;
  const centreDistance = Math.abs(
    a.top + a.height / 2 - (b.top + b.height / 2),
  );
  return centreDistance < Math.min(a.height, b.height) / 2;
};

/** Horizontal distance between two rects, negative when they overlap.
 *  `undefined` when either format carries no horizontal position. */
const horizontalGap = (a: Metrics, b: Metrics): number | undefined => {
  if (a.left === undefined || b.left === undefined) return undefined;
  return Math.max(b.left - (a.left + a.width), a.left - (b.left + b.width));
};

/**
 * Filters out rectangles that don't draw as readable marks:
 * - very thin vertical lines (column/table rules picked up by text selection)
 * - single-character orphan fragments left when a zone starts or ends
 *   mid-line, which render as a detached speck beside the real mark
 * - column rules that run far taller than the lines of text beside them,
 *   which draw as a band down the page from wherever the zone starts
 * - one-glyph rects split off by a font change mid-sentence (a `☐` checkbox
 *   inside a run of text gets its own text-layer span, so it gets its own
 *   tiny square mark). Only dropped when real text runs up against them, so
 *   a deliberate highlight of a single short word — or a short cell in a
 *   table row — still renders.
 *
 * Handles both coordinate formats:
 * - `{ width, height }` from backend/stored highlights
 * - `{ x1, y1, x2, y2 }` from newly created highlights
 *
 * Never empties a non-empty highlight: if every rect reads as a separator,
 * the usable originals come back instead. Rects with invalid dimensions are
 * always dropped, fallback included.
 *
 * @template T - Rectangle type extending Rect
 * @param rects - Array of rectangles to filter
 * @returns Filtered array with separator rectangles removed
 *
 * @example
 * ```ts
 * const filtered = filterSeparatorRects([
 *   { width: 100, height: 20 },  // kept
 *   { width: 2, height: 50 },    // removed (thin vertical line)
 * ]);
 * ```
 */
export const filterSeparatorRects = <T extends Rect>(rects: T[]): T[] => {
  const metrics = rects.map(measure);
  const lineHeight = textLineHeight(metrics);

  // Rects reach us in viewport pixels, so an absolute height grows with the
  // zoom level; measure against the highlight's own lines wherever we have
  // them, or the orphan rules switch themselves off on a zoomed page.
  const singleLineMaxHeight =
    lineHeight === undefined
      ? SINGLE_LINE_MAX_HEIGHT
      : lineHeight * MULTI_LINE_HEIGHT_RATIO;

  // Rectangles with zero or invalid dimensions. NaN needs its own check — it
  // fails every comparison, so `<= 0` alone would let it through. Kept apart
  // from the cosmetic verdicts below because the fallback never restores them.
  const isValid = metrics.map(
    ({ width, height }) =>
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      width > 0 &&
      height > 0,
  );

  // Decide per rect over `metrics`, so each entry is non-optional under
  // noUncheckedIndexedAccess, then map the verdicts back onto the input.
  const keep = metrics.map((self, index) => {
    const { width, height } = self;

    if (!isValid[index]) return false;

    // Filter out very thin vertical lines (aspect ratio < 0.1 and width < 10)
    if (width / height < 0.1 && width < 10) return false;

    // Column rules too wide for the absolute threshold above: measured against
    // the highlight's own line height, so it holds at any zoom or font size.
    if (
      lineHeight !== undefined &&
      height > lineHeight * MULTI_LINE_HEIGHT_RATIO &&
      width / height < VERTICAL_RULE_RATIO
    ) {
      return false;
    }

    // A tall narrow rect is covered by the rule above, not by the ones below.
    if (height >= singleLineMaxHeight) return true;

    // Sub-character speck — never readable, whatever it sits next to.
    if (width < height * ORPHAN_WIDTH_RATIO) return false;

    // One-glyph rect: a speck only when it interrupts a line of real text.
    // The text has to run right up against it — a wide rect further along the
    // same row is another table cell, and the glyph is that cell's content.
    if (width < height * GLYPH_WIDTH_RATIO) {
      const beside = metrics.some((other, j) => {
        if (j === index) return false;
        if (!sharesLine(self, other)) return false;
        if (other.width <= width * NEIGHBOUR_WIDTH_RATIO) return false;
        const gap = horizontalGap(self, other);
        return gap !== undefined && gap <= height * NEIGHBOUR_GAP_RATIO;
      });
      return !beside;
    }

    return true;
  });

  const filtered = rects.filter((_rect, index) => keep[index]);
  if (filtered.length > 0) return filtered;

  // Every rect read as a separator. Callers have no geometry left to draw or
  // to derive a bounding rect from — `Math.min()` of nothing is `Infinity`,
  // which JSON stores as `null` — so hand back what was usable: a misshapen
  // mark beats none.
  return rects.filter((_rect, index) => isValid[index]);
};
